"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { Badge } from "@travel/components/ui/badge";
import { TravelAPI, useAPI, useConfig } from "@travel/shared/data-access";
import { addDays, format, parseISO, startOfDay } from "date-fns";
import { CalendarDays, MapPin } from "lucide-react";
import { HotelCollectionList, PlaceCollectionList, } from "@travel/libs/shared/content/ui/common/collection-section/collection-list";
import CMSLandingSection, { CMSLandingHeading, } from "@travel/libs/shared/content/ui/landing/landing-section/landing-section";
import { ensureHTTPS } from "@travel/libs/shared/utils/strings";
export var getCollection = TravelAPI({
    name: "collection:fetch",
    url: function (request) { return "/api/collections?id=".concat(request.id); },
});
export function SDKCollection(_a) {
    var collectionID = _a.collectionID, title = _a.title, subtitle = _a.subtitle, showTitle = _a.showTitle, showSubtitle = _a.showSubtitle, showDates = _a.showDates, showPlaceNames = _a.showPlaceNames, showMore = _a.showMore, mutedHeading = _a.mutedHeading, props = __rest(_a, ["collectionID", "title", "subtitle", "showTitle", "showSubtitle", "showDates", "showPlaceNames", "showMore", "mutedHeading"]);
    var collectionId = collectionID;
    var config = useConfig();
    var _b = useAPI(collectionId ? { id: collectionId } : null, getCollection, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        baseURL: "https://travel-partner-one.vercel.app",
        //process.env.NEXT_PUBLIC_APP_DOMAIN ||
    }), data = _b.data, isLoading = _b.isLoading;
    var type = (data === null || data === void 0 ? void 0 : data.type) || "hotels";
    var collection = data === null || data === void 0 ? void 0 : data.collection;
    var places = data === null || data === void 0 ? void 0 : data.places;
    var items = data === null || data === void 0 ? void 0 : data.data;
    var formattedDateRange = useMemo(function () {
        if (!collection)
            return;
        var _a = __read(computeCollectionDateRange(collection), 2), startDate = _a[0], endDate = _a[1];
        var sameMonth = startDate.getMonth() === endDate.getMonth();
        var sameYear = startDate.getFullYear() === endDate.getFullYear();
        if (sameYear) {
            if (sameMonth) {
                return "".concat(format(startDate, "MMMM d"), " - ").concat(format(endDate, "d, yyyy"));
            }
            else {
                return "".concat(format(startDate, "MMMM d"), " - ").concat(format(endDate, "MMMM d, yyyy"));
            }
        }
        else {
            return "".concat(format(startDate, "MMMM d, yyyy"), " - ").concat(format(endDate, "MMMM d, yyyy"));
        }
    }, [collection]);
    title = title || (collection === null || collection === void 0 ? void 0 : collection.title);
    subtitle = subtitle || (collection === null || collection === void 0 ? void 0 : collection.subtitle);
    showTitle = typeof showTitle === "boolean" ? showTitle : !!title;
    showSubtitle = typeof showSubtitle === "boolean" ? showSubtitle : !!subtitle;
    var placeNames = (places === null || places === void 0 ? void 0 : places.map(function (place) {
        return (place === null || place === void 0 ? void 0 : place.displayName) ? place.displayName : place.name;
    })) || [];
    var canShowTitle = showTitle && !!title;
    var canShowSubtitle = showSubtitle && !!subtitle;
    var canShowDates = showDates && formattedDateRange;
    var canShowPlaceNames = showPlaceNames && placeNames.length > 0;
    var hasBackground = false;
    var lists = {
        cities: PlaceCollectionList,
        hotels: HotelCollectionList,
    };
    var List = lists[type];
    return collection ? (_jsx(CMSLandingSection, { header: _jsx(CMSLandingHeading, { title: canShowTitle ? title : undefined, titleClassName: canShowSubtitle ? "mb-2" : canShowDates ? "mb-4" : "mb-6", subtitle: canShowSubtitle ? subtitle : undefined, subtitleClassName: canShowDates || canShowPlaceNames ? "mb-2" : "mb-6", muted: mutedHeading, hasBackground: hasBackground }), body: _jsxs(_Fragment, { children: [canShowDates && (_jsx("div", { className: "mb-2", children: _jsxs(Badge, { variant: "secondary", className: "text-primary inline-flex items-center", children: [_jsx(CalendarDays, { className: "mr-1 !h-4 !w-4" }), formattedDateRange] }) })), canShowPlaceNames && (_jsx("div", { className: "flex gap-3 ".concat(!canShowDates ? "mt-2" : ""), children: [placeNames[0]].map(function (name) { return (_jsxs("p", { className: "".concat(hasBackground ? "text-white/80" : "text-muted-foreground", " flex items-center mb-6"), children: [_jsx(MapPin, { className: "mr-2 !h-4 !w-4" }), name] })); }) })), !!List && (_jsx(List, __assign({}, props, { className: "pb-2", items: items, sdk: true, baseURL: ensureHTTPS(config.domain) })))] }) })) : null;
}
export default SDKCollection;
export function computeCollectionDateRange(collection) {
    var start = collection.checkIn || format(startOfDay(new Date()), "yyyy-MM-dd");
    var end = collection.checkOut;
    var startOffset = Number(collection.checkInOffset);
    var endOffset = Number(collection.checkOutOffset);
    if (isNaN(startOffset))
        startOffset = 0;
    if (isNaN(endOffset))
        endOffset = 0;
    var startDate = addDays(parseISO(start), startOffset);
    var endDate = end || endOffset
        ? addDays(end ? parseISO(end) : startDate, endOffset)
        : addDays(startDate, 1);
    return [startDate, endDate];
}
